import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { subscribeDossierCallLock } from "@/lib/realtimeCallLock";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import RatingStars from "@/components/RatingStars";
import { toast } from "sonner";
import {
  Phone,
  PhoneOff,
  Lock,
  Mic,
  Loader2,
  CheckCircle2,
  XCircle,
  ShieldCheck,
  Smartphone,
  ArrowLeft,
  Star,
  AlertTriangle,
  Gavel,
  CalendarClock,
  Sparkles,
} from "lucide-react";
import { buildE164FromCountryAndNational, formatNationalPhoneInputLive } from "@/lib/phoneFormat";
import { PHONE_COUNTRY_OPTIONS, getPhoneCountryById } from "@/lib/countryDialCodes";
import { cn } from "@/lib/utils";
import { isDisputeOpeningBlockedByPlatform } from "@/lib/disputeEligibility";

/** Aligné avec l’API : type + explication écrite obligatoire, longueur totale minimale. */
const MIN_REFUSAL_PAYLOAD_LENGTH = 75;

const DEFAULT_PLATFORM_RULE_NOTICE =
  "Vous avez contrevenu aux règles de la plateforme : il est interdit de demander ou d’obtenir des coordonnées du client en dehors du canal prévu. Le vendeur a été rémunéré pour cet échange ; les informations du dossier sont débloquées comme après un RDV confirmé.";

/** Min. avant fin d’appel (acheteur pas encore décroché). */
const CONNECTING_END_MIN_MS = 20_000;
/** Min. après décroché acheteur : laisser le temps de composer et faire sonner le prospect. */
const CONNECTING_END_AFTER_BUYER_MS = 32_000;
/** Acheteur a décroché mais la 2ᵉ jambe (prospect) n’apparaît pas encore — laisser le webhook Telnyx finir. */
const CONNECTING_END_BUYER_NO_CLIENT_MS = 18_000;
/** Si l’acheteur n’a même pas été détecté comme décroché. */
const CONNECTING_END_NO_PROSPECT_MS = 6_000;

const REFUSAL_TYPE_LABELS = {
  pas_interesse: "Client pas intéressé",
  injoignable: "Client injoignable",
  faux_numero: "Faux numéro / erreur",
  deja_contacte: "Déjà contacté par un autre",
  hors_cible: "Hors cible",
  autre: "Autre",
};

function refusalVerdictHeadline(decision) {
  if (decision === "buyer_refund") return "Décision automatique : remboursement en votre faveur.";
  if (decision === "payment_released") {
    return "Paiement déjà annulé côté banque : vous n’êtes pas débité.";
  }
  if (decision === "seller_capture") return "Décision automatique : versement au vendeur (refus non retenu par l’analyse).";
  if (decision === "litige_gray_zone_prospect") {
    return "Analyse en zone grise : un litige a été ouvert pour examen par l’équipe (citations et transcription joints).";
  }
  return "Décision enregistrée suite à l’analyse de l’appel.";
}

/** Sous-chemin /call/:id/m = appel mobile (ne dépend pas des ?…, souvent perdus par les apps / navigateur). */
function isCallMobilePathname(pathname) {
  return typeof pathname === "string" && /^\/call\/[^/]+\/m\/?$/.test(pathname);
}

/**
 * Parcours acheteur : saisie ligne pour rappel Telnyx, appel, fin, notation.
 * @param {{ transactionId: string, variant?: "page" | "embedded", openOutcomeOnMount?: boolean, forceRetry?: boolean, onRequestExit?: () => void, onRequestOpenDispute?: (p: { transactionId: string, defaultDescription: string }) => void }} props
 */
export function BuyerCallSession({
  transactionId,
  variant = "page",
  openOutcomeOnMount = false,
  forceRetry = false,
  onRequestExit,
  onRequestOpenDispute,
}) {
  const navigate = useNavigate();
  const location = useLocation();
  /** Ne pas utiliser useSearchParams() : avec RR v7 / modale + portal ça peut faire planter le rendu ; location.search suffit. */
  const searchParams = useMemo(() => new URLSearchParams(location.search || ""), [location.search]);
  const { user } = useAuth();
  const callOpts = useMemo(() => ({ handoffTransactionId: transactionId }), [transactionId]);
  const mergeCallContext = useCallback((data) => {
    if (!data) return;
    if (data.dossier_id != null) setDossierId(String(data.dossier_id));
    if (data.call_certified_at) setCallCertifiedAt(data.call_certified_at);
    if (data.call_blocked != null) setCallBlocked(Boolean(data.call_blocked));
    if (data.client_answered != null) setClientAnswered(Boolean(data.client_answered));
    setDeferInfo({
      canDefer: Boolean(data.can_request_callback_defer),
      reservedUntil: data.dossier_reserved_until ?? null,
      deferCount: Number(data.reservation_defer_count) || 0,
      deferMax: Number(data.reservation_defer_max) || 3,
      extHours: Number(data.callback_defer_extension_hours) || 24,
    });
    if (data.call_flow === "prospect_first" || data.call_flow === "buyer_first") {
      setCallFlow(data.call_flow);
    }
    if (data.buyer_rdv_decision_deadline_at != null) {
      setBuyerRdvDeadlineAt(data.buyer_rdv_decision_deadline_at);
    }
    if (data.buyer_rdv_decision_hours != null) {
      setBuyerRdvDecisionHours(Number(data.buyer_rdv_decision_hours) || 12);
    }
  }, []);
  const isEmbedded = variant === "embedded";
  /** Au-dessus du panneau d’appel inline du dashboard (z-100) : wrapper + overlay > 100. */
  const stackedFromShell = isEmbedded
    ? {
        overlayClassName: "z-[140]",
        wrapperClassName: "z-[141]",
        className: "z-[141] max-h-[90vh] overflow-x-hidden overflow-y-auto",
      }
    : {};

  const leaveDashboard = () => {
    if (isEmbedded && typeof onRequestExit === "function") {
      onRequestExit();
      return;
    }
    navigate("/dashboard");
  };

  const stepRef = useRef("mobile_input");
  const connectingSinceRef = useRef(0);

  // step: mobile_input | connecting | in_call | ended — rappel Telnyx sur le mobile de l’acheteur uniquement.
  const [step, setStep] = useState("mobile_input");
  const [phoneCountryId, setPhoneCountryId] = useState("FR");
  const [buyerNational, setBuyerNational] = useState("");
  const [callId, setCallId] = useState(null);
  const [callSessionId, setCallSessionId] = useState(null);

  useEffect(() => {
    stepRef.current = step;
  }, [step]);

  const contextPollParams = useMemo(() => {
    const p = { transactionId };
    if (callSessionId) p.sessionId = callSessionId;
    return p;
  }, [transactionId, callSessionId]);

  const [duration, setDuration] = useState(0);
  const [showEnd, setShowEnd] = useState(false);
  const [showAiAnalysis, setShowAiAnalysis] = useState(false);
  const [awaitingPhoneHangup, setAwaitingPhoneHangup] = useState(false);
  const [gotAppointment, setGotAppointment] = useState(null);
  const [refusalReason, setRefusalReason] = useState("");
  const [refusalDetails, setRefusalDetails] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [aiResult, setAiResult] = useState(null);
  const [mobileMessage, setMobileMessage] = useState("");
  const [showRating, setShowRating] = useState(false);
  /** Violation plateforme : modale pleine avant la notation (évite toast latéral peu lisible). */
  const [platformRuleModalOpen, setPlatformRuleModalOpen] = useState(false);
  const [platformRuleModalText, setPlatformRuleModalText] = useState("");
  const [ratingStars, setRatingStars] = useState(0);
  const [submittingRating, setSubmittingRating] = useState(false);
  const timerRef = useRef(null);
  const [dossierId, setDossierId] = useState(null);
  const [callBlocked, setCallBlocked] = useState(false);
  const [showCallCert, setShowCallCert] = useState(false);
  const [callCert, setCallCert] = useState({
    c1: false,
    c2: false,
    c3: false,
    c4: false,
    c5: false,
  });
  const [pendingStart, setPendingStart] = useState(null); // () => Promise<void>
  const [callCertifiedAt, setCallCertifiedAt] = useState(null);
  const [buyerRdvDeadlineAt, setBuyerRdvDeadlineAt] = useState(null);
  const [buyerRdvDecisionHours, setBuyerRdvDecisionHours] = useState(12);
  const [deferInfo, setDeferInfo] = useState({
    canDefer: false,
    reservedUntil: null,
    deferCount: 0,
    deferMax: 3,
    extHours: 24,
  });
  const [deferDialogOpen, setDeferDialogOpen] = useState(false);
  const [deferNote, setDeferNote] = useState("");
  const [deferSubmitting, setDeferSubmitting] = useState(false);
  /** Refus : préparation enregistrement Telnyx puis analyse IA sur /calls/end — affichage explicite dans le modal. */
  const [postCallSubmitPhase, setPostCallSubmitPhase] = useState(null);
  const [callAnswered, setCallAnswered] = useState(false);
  const [queueRetryIn, setQueueRetryIn] = useState(0);
  const queueTimerRef = useRef(null);
  const [clientAnswered, setClientAnswered] = useState(false);
  /** Après refus : verdict IA puis choix OK / litige avant la notation. */
  const [showPostRefusalVerdict, setShowPostRefusalVerdict] = useState(false);
  const [lastOutcomeDecision, setLastOutcomeDecision] = useState(null);
  /** prospect_first (défaut) | buyer_first */
  const [callFlow, setCallFlow] = useState("prospect_first");

  useEffect(() => {
    if (openOutcomeOnMount) {
      if (timerRef.current) clearInterval(timerRef.current);
      setDuration(0);
      setStep("ended");
      stepRef.current = "ended";
      setShowEnd(true);
      return;
    }
    setShowEnd(false);
    setShowAiAnalysis(false);
    setAwaitingPhoneHangup(false);
    if (stepRef.current === "ended") {
      setStep("mobile_input");
      stepRef.current = "mobile_input";
    }
  }, [openOutcomeOnMount]);

  useEffect(() => {
    if (isEmbedded) return;
    const h = searchParams.get("h");
    if (!h || !transactionId) return;
    try {
      sessionStorage.setItem(`rdm_call_h_${transactionId}`, h);
      sessionStorage.setItem(`rdm_call_mobile_${transactionId}`, "1");
    } catch {
      /* ignore */
    }
    const next = new URLSearchParams(searchParams);
    next.delete("h");
    const basePath = isCallMobilePathname(location.pathname)
      ? `/call/${transactionId}/m`
      : `/call/${transactionId}`;
    navigate({ pathname: basePath, search: next.toString() ? `?${next}` : "" }, { replace: true });
  }, [isEmbedded, transactionId, searchParams, navigate, location.pathname]);

  useEffect(() => {
    if (isEmbedded) return;
    if (!transactionId) return;
    if (
      isCallMobilePathname(location.pathname) ||
      searchParams.get("mobile") === "1" ||
      searchParams.get("h")
    ) {
      try {
        sessionStorage.setItem(`rdm_call_mobile_${transactionId}`, "1");
      } catch {
        /* ignore */
      }
    }
  }, [isEmbedded, transactionId, searchParams, location.pathname]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      if (!transactionId) return;
      try {
        const { data } = await api.get("/call/context", { params: { transactionId }, ...callOpts });
        if (cancelled || !data) return;
        mergeCallContext(data);
      } catch {
        /* 401 : pas de session ni jeton — géré par la route */
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [transactionId, callOpts, mergeCallContext]);

  /** Engagement déjà enregistré sur cette commande → ne pas redemander les 5 cases à chaque relance. */
  useEffect(() => {
    if (!callCertifiedAt) return;
    setCallCert({ c1: true, c2: true, c3: true, c4: true, c5: true });
  }, [callCertifiedAt]);

  useEffect(() => {
    if (!dossierId || !transactionId) return undefined;
    const handoffOnly =
      typeof window !== "undefined" &&
      !user &&
      window.sessionStorage.getItem(`rdm_call_h_${transactionId}`);
    if (handoffOnly) {
      const id = setInterval(async () => {
        try {
          const { data } = await api.get("/call/context", { params: { transactionId }, ...callOpts });
          if (data) mergeCallContext(data);
        } catch {
          /* ignore */
        }
      }, 8000);
      return () => clearInterval(id);
    }
    return subscribeDossierCallLock(dossierId, setCallBlocked, {
      excludeTransactionId: transactionId,
      anyInProgress: false,
      uniqueSuffix: `call-session-${transactionId}`,
    });
  }, [dossierId, transactionId, user, callOpts, mergeCallContext]);

  /** À l’ouverture du modal de fin d’appel : rafraîchir éligibilité prolongation (décroché client, plafond…). */
  useEffect(() => {
    if (!showEnd || !transactionId) return undefined;
    let cancelled = false;
    void (async () => {
      try {
        const { data } = await api.get("/call/context", { params: { transactionId }, ...callOpts });
        if (!cancelled && data) mergeCallContext(data);
      } catch {
        /* ignore */
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [showEnd, transactionId, callOpts, mergeCallContext]);

  const certifyCall = async () => {
    const { data } = await api.post("/call/certify", { transaction_id: transactionId }, callOpts);
    if (data?.call_certified_at) setCallCertifiedAt(data.call_certified_at);
  };

  const releaseCallLock = useCallback(async () => {
    if (!transactionId) return;
    try {
      await api.post("/call/abort", { transaction_id: transactionId }, callOpts);
      setCallBlocked(false);
    } catch {
      /* ignore — filet côté webhook Telnyx */
    }
  }, [transactionId, callOpts]);

  const allCallChecked = callCert.c1 && callCert.c2 && callCert.c3 && callCert.c4 && callCert.c5;

  const startLocalTimer = useCallback(() => {
    if (timerRef.current) clearInterval(timerRef.current);
    setDuration(0);
    timerRef.current = setInterval(() => setDuration((d) => d + 1), 1000);
  }, []);

  const clearQueueTimer = useCallback(() => {
    if (queueTimerRef.current) window.clearInterval(queueTimerRef.current);
    queueTimerRef.current = null;
    setQueueRetryIn(0);
  }, []);

  const startMobileCall = async ({ certifyIfNeeded = false } = {}) => {
    if (callBlocked) return toast.error("Un concurrent est déjà en ligne avec ce client.");
    let buyer_phone;
    try {
      buyer_phone = buildE164FromCountryAndNational(getPhoneCountryById(phoneCountryId), buyerNational);
    } catch (e) {
      return toast.error(e?.message || "Numéro invalide. Vérifiez le pays et le numéro local.");
    }
    if (certifyIfNeeded && !callCertifiedAt) {
      try {
        await certifyCall();
      } catch (certErr) {
        toast.error(certErr?.response?.data?.detail || certErr?.message || "Engagement non enregistré");
        return;
      }
    }
    setShowEnd(false);
    setShowAiAnalysis(false);
    setAwaitingPhoneHangup(false);
    setGotAppointment(null);
    setCallId(null);
    setCallSessionId(null);
    setStep("connecting");
    stepRef.current = "connecting";
    connectingSinceRef.current = Date.now();
    setCallAnswered(false);
    setClientAnswered(false);
    clearQueueTimer();
    try {
      const { data } = await api.post(
        `/calls/start/${transactionId}`,
        { mode: "mobile", buyer_phone, transactionId, force_retry: forceRetry },
        callOpts,
      );
      if (data.session_id) setCallSessionId(String(data.session_id));
      setCallId(data.call_id);
      if (data.call_flow === "prospect_first" || data.call_flow === "buyer_first") {
        setCallFlow(data.call_flow);
      }
      setMobileMessage(data.message || "");
      toast.success(
        data.message ||
          (data.call_flow === "prospect_first"
            ? "Contact du prospect en cours — votre mobile sonnera s’il répond."
            : "Telnyx compose votre numéro · décrochez pour être mis en relation."),
      );
    } catch (e) {
      const status = e?.response?.status;
      if (status === 410) {
        const detail =
          (typeof e?.response?.data?.detail === "string" && e.response.data.detail.trim()) ||
          "Le délai pour appeler est expiré (maximum 12h après l'achat). L’annonce a été remise sur la marketplace.";
        setMobileMessage(detail);
        toast.error(detail);
        await releaseCallLock();
        connectingSinceRef.current = 0;
        setStep("mobile_input");
        return;
      }
      if (status === 409) {
        const detail =
          (typeof e?.response?.data?.detail === "string" && e.response.data.detail.trim()) ||
          "Annonce déjà réservée par un autre acheteur. Réessayez plus tard.";
        setMobileMessage(detail);
        toast.error(detail);
        await releaseCallLock();
        connectingSinceRef.current = 0;
        setStep("mobile_input");
        return;
      }
      if (status === 401 || status === 403) {
        const detail =
          (typeof e?.response?.data?.detail === "string" && e.response.data.detail.trim()) ||
          "Vous n’êtes pas autorisé à lancer cet appel. Reconnectez-vous puis réessayez.";
        setMobileMessage(detail);
        toast.error(detail);
        await releaseCallLock();
        connectingSinceRef.current = 0;
        setStep("mobile_input");
        return;
      }
      const retryAfter =
        Number(e?.response?.data?.retry_after_seconds) ||
        Number(e?.response?.headers?.["retry-after"]) ||
        0;
      if (status === 429 && retryAfter > 0) {
        const seconds = Math.max(5, Math.min(180, Math.floor(retryAfter)));
        setMobileMessage("Trop d'appels en cours. Mise en attente…");
        setQueueRetryIn(seconds);
        queueTimerRef.current = window.setInterval(() => {
          setQueueRetryIn((s) => {
            if (s <= 1) {
              if (queueTimerRef.current) window.clearInterval(queueTimerRef.current);
              queueTimerRef.current = null;
              // relance automatique
              void startMobileCall();
              return 0;
            }
            return s - 1;
          });
        }, 1000);
        return;
      }
      const detail =
        (typeof e?.response?.data?.detail === "string" && e.response.data.detail.trim()) ||
        (typeof e?.message === "string" && e.message.trim()) ||
        (e?.response?.status ? `Erreur serveur (${e.response.status})` : "");
      toast.error(detail || "Impossible de lancer l'appel");
      await releaseCallLock();
      connectingSinceRef.current = 0;
      setStep("mobile_input");
    }
  };

  const openPostCallOutcome = useCallback(() => {
    if (timerRef.current) clearInterval(timerRef.current);
    clearQueueTimer();
    setAwaitingPhoneHangup(false);
    setCallId(null);
    setCallSessionId(null);
    connectingSinceRef.current = 0;
    setStep("ended");
    setShowEnd(true);
  }, [clearQueueTimer]);

  const hangUp = openPostCallOutcome;

  const requestPhoneHangup = useCallback(() => {
    setAwaitingPhoneHangup(true);
    toast.info("Raccrochez sur votre téléphone mobile. La suite s’affichera dès que l’appel est terminé.");
  }, []);

  const abortFailedCall = useCallback(
    async (userMessage) => {
      if (timerRef.current) clearInterval(timerRef.current);
      clearQueueTimer();
      setCallId(null);
      setCallSessionId(null);
      connectingSinceRef.current = 0;
      setCallAnswered(false);
      setClientAnswered(false);
      await releaseCallLock();
      setStep("mobile_input");
      stepRef.current = "mobile_input";
      toast.info(
        userMessage ||
          "Appel terminé sans mise en relation avec le prospect. Vous pouvez réessayer depuis « Mes achats ».",
      );
    },
    [clearQueueTimer, releaseCallLock],
  );

  const submitDeferClientCallback = async () => {
    if (!transactionId || deferSubmitting) return;
    setDeferSubmitting(true);
    try {
      const { data } = await api.post(
        "/call/defer-client-callback",
        { transaction_id: transactionId, note: deferNote.trim() || undefined },
        callOpts,
      );
      setDeferNote("");
      setDeferDialogOpen(false);
      toast.success(
        `Réservation prolongée d’environ ${data?.extension_hours ?? 24} h. Le vendeur a été informé (message sur le dossier ; e-mail si Resend est configuré côté serveur).`,
      );
      try {
        const { data: ctx } = await api.get("/call/context", { params: { transactionId }, ...callOpts });
        mergeCallContext(ctx);
      } catch {
        /* ignore */
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Impossible d’enregistrer la prolongation.");
    } finally {
      setDeferSubmitting(false);
    }
  };

  // Pendant connecting ou in_call : fin de session Telnyx (raccrochage) + en connecting passage in_call quand le client est bridgé.
  useEffect(() => {
    if (step !== "connecting" && step !== "in_call") return undefined;
    if (!transactionId) return undefined;
    let cancelled = false;
    const id = window.setInterval(async () => {
      try {
        const { data } = await api.get("/call/context", { params: contextPollParams, ...callOpts });
        if (cancelled || !data) return;
        mergeCallContext(data);
        const curStep = stepRef.current;
        if (curStep === "connecting" && data.prospect_unreachable_reason) {
          if (
            !callSessionId ||
            (data.call_session_id && String(data.call_session_id) !== String(callSessionId))
          ) {
            return;
          }
          const reason = data.prospect_unreachable_reason;
          if (reason === "voicemail") {
            await abortFailedCall(
              "Répondeur détecté chez le client. Réessayez plus tard depuis « Mes achats ».",
            );
          } else if (reason === "bridge_timeout") {
            await abortFailedCall(
              "Le client a répondu mais vous n’avez pas décroché à temps sur votre mobile. Réessayez et décrochez dès que votre téléphone sonne.",
            );
          } else if (reason === "no_answer") {
            await abortFailedCall(
              "Le prospect n’a pas répondu. Réessayez plus tard depuis « Mes achats ».",
            );
          } else {
            await abortFailedCall();
          }
          return;
        }
        if (data.call_session_active) {
          if (curStep === "connecting" && data.client_answered) {
            setStep("in_call");
            stepRef.current = "in_call";
            startLocalTimer();
          }
          return;
        }
        if (data.call_session_ended) {
          if (
            callSessionId &&
            data.call_session_id &&
            String(data.call_session_id) !== String(callSessionId)
          ) {
            return;
          }
          /** Nouvel appel : ignorer la fin de la session précédente tant que le nouvel id n’est pas connu. */
          if (!callSessionId && curStep === "connecting") {
            return;
          }
          if (curStep === "connecting" && !data.client_answered) {
            const elapsed = Date.now() - (connectingSinceRef.current || 0);
            const prospectDialed = Boolean(data.prospect_dialed);
            const minWait = prospectDialed
              ? data.call_answered
                ? CONNECTING_END_AFTER_BUYER_MS
                : CONNECTING_END_MIN_MS
              : data.call_answered
                ? CONNECTING_END_BUYER_NO_CLIENT_MS
                : CONNECTING_END_NO_PROSPECT_MS;
            if (elapsed < minWait) return;
            let failMsg = null;
            if (!prospectDialed) {
              failMsg =
                data.prospect_phone_ok === false
                  ? "Numéro du prospect invalide ou manquant sur ce dossier. Le vendeur doit corriger la fiche avant un nouvel appel."
                  : "Impossible de joindre le prospect (réseau Telnyx ou numéro). Réessayez l’appel dans quelques instants.";
            } else if (data.call_flow === "prospect_first" && !data.call_answered) {
              failMsg =
                "Le prospect n’a pas répondu ou l’appel a été coupé avant la mise en relation. Réessayez depuis « Mes achats ».";
            } else if (data.call_flow === "prospect_first" && data.call_answered) {
              failMsg =
                "Vous n’avez pas décroché à temps sur votre mobile après la réponse du client. Réessayez et décrochez dès que votre téléphone sonne.";
            }
            await abortFailedCall(failMsg);
            return;
          }
          hangUp();
          return;
        }
        if (curStep === "connecting") {
          if (data.call_answered != null) setCallAnswered(Boolean(data.call_answered));
          if (data.client_answered != null) setClientAnswered(Boolean(data.client_answered));
          if (data.client_answered) {
            setStep("in_call");
            stepRef.current = "in_call";
            startLocalTimer();
          }
        }
      } catch {
        /* ignore */
      }
    }, 1000);
    return () => {
      cancelled = true;
      window.clearInterval(id);
    };
  }, [
    step,
    transactionId,
    callOpts,
    contextPollParams,
    callSessionId,
    hangUp,
    abortFailedCall,
    startLocalTimer,
    mergeCallContext,
  ]);

  useEffect(() => () => {
    if (timerRef.current) clearInterval(timerRef.current);
    if (queueTimerRef.current) window.clearInterval(queueTimerRef.current);
  }, []);

  const submitOutcome = async () => {
    if (gotAppointment === null) return toast.error("Répondez à la question");
    if (!gotAppointment && !refusalReason) return toast.error("Choisissez un type de refus.");
    let refusalPayload = "";
    if (!gotAppointment) {
      const expl = refusalDetails.trim();
      if (!expl) {
        return toast.error("Expliquez obligatoirement pourquoi le RDV n’a pas eu lieu (texte libre).");
      }
      const typeLabel = REFUSAL_TYPE_LABELS[refusalReason] || refusalReason;
      refusalPayload = `Type : ${typeLabel}. Explication de l’acheteur : ${expl}`;
      if (refusalPayload.length < MIN_REFUSAL_PAYLOAD_LENGTH) {
        return toast.error(
          "Votre explication est trop courte : décrivez ce qui s’est passé pendant l’appel (quelques phrases minimum).",
        );
      }
    }
    setSubmitting(true);
    setPostCallSubmitPhase(null);
    const refusalFlow = !gotAppointment;
    if (refusalFlow) {
      setPostCallSubmitPhase("waiting_recording");
      setShowEnd(false);
      setShowAiAnalysis(true);
    }
    try {
      /** Refus : attendre l’enregistrement puis analyse IA. RDV confirmé : pas d’attente. */
      if (refusalFlow) {
        const waitCapMs = 120_000;
        const pollMs = 2000;
        const waitStart = Date.now();
        let ready = false;
        while (Date.now() - waitStart < waitCapMs) {
          const { data: ctxData } = await api.get("/call/context", { params: { transactionId }, ...callOpts });
          if (ctxData?.recording_ready) {
            ready = true;
            break;
          }
          await new Promise((r) => setTimeout(r, pollMs));
        }
        if (!ready) {
          setShowAiAnalysis(false);
          setShowEnd(true);
          toast.error(
            "L’enregistrement n’est pas encore prêt. Patientez encore un peu puis réessayez « Confirmer » ; si le blocage continue, contactez le support.",
          );
          return;
        }
        setPostCallSubmitPhase("ai_analysis");
      }

      const payload = {
        transaction_id: transactionId,
        got_appointment: gotAppointment,
        outcome_kind: gotAppointment ? "rdv_confirmed" : "dossier_refusal",
      };
      if (!gotAppointment) payload.refusal_reason = refusalPayload;
      const { data } = await api.post(`/calls/end/${transactionId}`, payload, callOpts);
      if (data.ai_analysis) setAiResult(data.ai_analysis);

      const platformViolation = data.decision === "platform_violation_contact_harvest";
      const refusalVerdictStep = !gotAppointment && !platformViolation;
      setLastOutcomeDecision(data.decision ?? null);

      if (platformViolation) {
        setPlatformRuleModalText(String(data.buyer_notice || "").trim() || DEFAULT_PLATFORM_RULE_NOTICE);
        setPlatformRuleModalOpen(true);
      }

      if (data.dossier_archived_after_refusals) {
        toast.warning(
          "L’annonce a été retirée du catalogue : seuil d’achats refus successifs atteint (règles d’usage RdvMarket).",
          { duration: 9000 },
        );
      }
      if (data.dossier_archived_for_prospect) {
        toast.warning(
          data.decision === "litige_gray_zone_prospect"
            ? "L’annonce a été retirée du catalogue et le vendeur a été informé par e-mail (sans mention de votre identité). Le litige ouvert concerne uniquement le séquestre, à trancher par l’équipe."
            : "L’annonce a été retirée du catalogue suite à l’analyse automatique de l’échange (qualification).",
          { duration: 9000 },
        );
      }
      if (data.decision === "litige_gray_zone_prospect" && data.dispute_opened) {
        toast.warning(
          "Analyse en zone grise : un litige a été ouvert automatiquement. L’équipe examinera la transcription et les citations — suivez l’avancement depuis « Mes achats ».",
          { duration: 11000 },
        );
      }
      if (data.decision === "payment_released") {
        toast.info(String(data.detail || "Autorisation déjà annulée : aucun débit."), { duration: 9000 });
      }
      if (!platformViolation) {
        if (refusalVerdictStep) {
          toast.success(
            data.decision === "litige_gray_zone_prospect"
              ? "Analyse terminée : litige ouvert pour examen humain."
              : data.decision === "payment_released"
                ? "Analyse terminée — voir le message sur le paiement."
                : "Analyse terminée : consultez le verdict ci-dessous.",
          );
        } else {
          toast.success(
            gotAppointment
              ? "RDV confirmé : fonds libérés. Les infos client sont maintenant dans « Mes achats »."
              : data.decision === "buyer_refund"
                ? "Refus analysé : remboursement en cours."
                : "Appel clôturé",
          );
        }
      }
      setShowEnd(false);
      setShowAiAnalysis(false);
      if (platformViolation) {
        setShowRating(false);
        setShowPostRefusalVerdict(false);
      } else if (refusalVerdictStep) {
        setShowPostRefusalVerdict(true);
        setShowRating(false);
      } else {
        setShowPostRefusalVerdict(false);
        setShowRating(true);
      }
    } catch (e) {
      if (refusalFlow) {
        setShowAiAnalysis(false);
        setShowEnd(true);
      }
      toast.error(e.response?.data?.detail || "Erreur");
    } finally {
      setSubmitting(false);
      setPostCallSubmitPhase(null);
    }
  };

  const submitRating = async () => {
    if (ratingStars < 1) return toast.error("Choisissez une note (1 à 5 étoiles)");
    setSubmittingRating(true);
    try {
      const hasHandoff =
        typeof window !== "undefined" && window.sessionStorage.getItem(`rdm_call_h_${transactionId}`);
      let payload = null;
      if (hasHandoff) {
        const r = await api.post(
          "/call/rating",
          { transaction_id: transactionId, stars: ratingStars, comment: "" },
          callOpts,
        );
        payload = r?.data ?? r;
      } else {
        const r = await api.post(`/ratings`, {
          transaction_id: transactionId,
          stars: ratingStars,
          comment: "",
        });
        payload = r?.data ?? r;
      }
      if (payload?.alreadyRated) {
        toast.info("Vous aviez déjà noté cette transaction.");
      } else {
        toast.success("Merci pour votre évaluation du vendeur");
      }
      setShowRating(false);
      setTimeout(() => leaveDashboard(), 800);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erreur lors de la notation");
    } finally {
      setSubmittingRating(false);
    }
  };

  const skipRating = () => {
    setShowRating(false);
    leaveDashboard();
  };

  const mm = String(Math.floor(duration / 60)).padStart(2, "0");
  const ss = String(duration % 60).padStart(2, "0");

  const shellClass =
    isEmbedded
      ? "max-w-xl mx-auto"
      : "max-w-xl mx-auto px-6 py-12";

  const handleLaunchCallClick = async () => {
    if (callBlocked) return;
    if (!String(buyerNational || "").trim()) {
      toast.error("Saisissez votre numéro mobile.");
      return;
    }
    try {
      buildE164FromCountryAndNational(getPhoneCountryById(phoneCountryId), buyerNational);
    } catch (e) {
      toast.error(e?.message || "Numéro invalide. Vérifiez le pays et le numéro local.");
      return;
    }
    if (callCertifiedAt) {
      void startMobileCall();
      return;
    }
    if (isEmbedded) {
      if (!allCallChecked) {
        toast.error("Cochez les 5 engagements ci-dessous avant de lancer l’appel.");
        return;
      }
      void startMobileCall({ certifyIfNeeded: true });
      return;
    }
    setPendingStart(() => () => startMobileCall({ certifyIfNeeded: true }));
    setShowCallCert(true);
  };

  const callCertFields = (
    <div className="space-y-3">
      <label className={`flex items-start gap-3 rounded-lg border-2 border-[var(--lm-border)] p-3 cursor-pointer transition-all hover:border-slate-300 ${callCert.c1 ? "lm-cert-option--on" : ""}`}>
        <input type="checkbox" checked={callCert.c1} onChange={(e) => setCallCert({ ...callCert, c1: e.target.checked })} className="accent-lm-primary mt-0.5 h-4 w-4 shrink-0 cursor-pointer" />
        <span className="text-xs text-slate-700 leading-relaxed">J&apos;ai lu et accepté les critères de qualification de ce dossier</span>
      </label>
      <label className={`flex items-start gap-3 rounded-lg border-2 border-[var(--lm-border)] p-3 cursor-pointer transition-all hover:border-slate-300 ${callCert.c2 ? "lm-cert-option--on" : ""}`}>
        <input type="checkbox" checked={callCert.c2} onChange={(e) => setCallCert({ ...callCert, c2: e.target.checked })} className="accent-lm-primary mt-0.5 h-4 w-4 shrink-0 cursor-pointer" />
        <span className="text-xs text-slate-700 leading-relaxed">
          Je m&apos;engage à <strong className="font-semibold text-slate-800">statuer</strong> (valider ou refuser le rendez-vous){" "}
          <strong className="font-semibold text-slate-800">dans les 12 heures</strong> suivant l&apos;appel. Passé ce délai,{" "}
          <strong className="font-semibold text-slate-800">tous les enregistrements seront analysés</strong> pour vérifier l&apos;absence de
          contournement ; si je n&apos;ai pas statué, le rendez-vous sera{" "}
          <strong className="font-semibold text-slate-800">réputé validé</strong>, avec possibilité d&apos;ouvrir un{" "}
          <strong className="font-semibold text-slate-800">litige</strong> dans le délai affiché après clôture (sauf violation constatée).
        </span>
      </label>
      <label
        className={`flex items-start gap-3 rounded-lg border-2 border-red-200/90 bg-red-50/70 p-3 cursor-pointer transition-all hover:border-red-300 ${callCert.c5 ? "border-red-400 bg-red-100/80 ring-1 ring-red-200/80" : ""}`}
      >
        <input type="checkbox" checked={callCert.c5} onChange={(e) => setCallCert({ ...callCert, c5: e.target.checked })} className="accent-red-600 mt-0.5 h-4 w-4 shrink-0 cursor-pointer" data-testid="call-cert-identity-rdv" />
        <span className="text-xs text-slate-700 leading-relaxed">
          Toute <strong className="font-semibold text-slate-800">récupération d&apos;informations sur l&apos;identité du client</strong> (nom,
          prénom, numéro, adresse e-mail, etc.) sera{" "}
          <strong className="font-semibold text-red-950">automatiquement considérée comme une confirmation du RDV</strong>.
        </span>
      </label>
      <label className={`flex items-start gap-3 rounded-lg border-2 border-[var(--lm-border)] p-3 cursor-pointer transition-all hover:border-slate-300 ${callCert.c3 ? "lm-cert-option--on" : ""}`}>
        <input type="checkbox" checked={callCert.c3} onChange={(e) => setCallCert({ ...callCert, c3: e.target.checked })} className="accent-lm-primary mt-0.5 h-4 w-4 shrink-0 cursor-pointer" />
        <span className="text-xs text-slate-700 leading-relaxed">
          Toute tentative de contacter le prospect en dehors de la plateforme entraîne la perte définitive du remboursement et le bannissement de mon compte
        </span>
      </label>
      <label className={`flex items-start gap-3 rounded-lg border-2 border-[var(--lm-border)] p-3 cursor-pointer transition-all hover:border-slate-300 ${callCert.c4 ? "lm-cert-option--on" : ""}`}>
        <input type="checkbox" checked={callCert.c4} onChange={(e) => setCallCert({ ...callCert, c4: e.target.checked })} className="accent-lm-primary mt-0.5 h-4 w-4 shrink-0 cursor-pointer" />
        <span className="text-xs text-slate-700 leading-relaxed">
          Tout refus jugé abusif ou de mauvaise foi entraîne la perte du remboursement et peut conduire au bannissement du compte
        </span>
      </label>
    </div>
  );

  return (
    <>
      <div className={shellClass}>
        <div className="bg-white border border-[#E5E7EB] rounded-2xl p-8 sm:p-10">
          <div className="inline-flex items-center gap-2 bg-[#2D7FF9]/10 text-[#2D7FF9] px-3 py-1 rounded-full text-xs font-bold tracking-wider uppercase mb-6">
            <Lock className="w-3.5 h-3.5" />Anonymisation totale · Appel sécurisé
          </div>

          {step === "mobile_input" && (
            <>
              <div className="text-center mb-6">
                <div className="w-16 h-16 mx-auto bg-[#2D7FF9]/10 rounded-2xl flex items-center justify-center mb-4">
                  <Smartphone className="w-8 h-8 text-[#2D7FF9]" />
                </div>
                <h1 className="font-display font-black text-2xl tracking-tighter mb-2">Entrez votre numéro mobile</h1>
                <p className="text-slate-500 text-sm">
                  Même depuis un ordinateur, l’appel passe par un <strong className="font-medium text-slate-700">rappel sur votre ligne</strong> : la
                  plateforme vous appelle, puis vous met en relation avec le prospect (numéros anonymisés).
                </p>
              </div>
              <div className="space-y-3">
                {callBlocked && (
                  <p className="text-sm text-amber-800 text-center">
                    ⏳ Ce client est en ligne avec un concurrent. Réessayez dans quelques minutes.
                  </p>
                )}
                <div className="flex flex-col gap-2 sm:flex-row sm:items-stretch">
                  <Select
                    value={phoneCountryId}
                    onValueChange={(v) => {
                      setPhoneCountryId(v);
                      setBuyerNational("");
                    }}
                    disabled={callBlocked}
                  >
                    <SelectTrigger className="h-14 w-full sm:w-[220px] shrink-0 font-mono text-sm" data-testid="buyer-phone-country">
                      <SelectValue placeholder="Pays" />
                    </SelectTrigger>
                    <SelectContent className="max-h-72">
                      {PHONE_COUNTRY_OPTIONS.map((opt) => (
                        <SelectItem key={opt.id} value={opt.id}>
                          {opt.name} (+{opt.dial})
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <Input
                    type="tel"
                    inputMode="tel"
                    placeholder={phoneCountryId === "FR" ? "06 12 34 56 78" : "Numéro local"}
                    value={buyerNational}
                    onChange={(e) =>
                      setBuyerNational(formatNationalPhoneInputLive(getPhoneCountryById(phoneCountryId), e.target.value))
                    }
                    className="h-14 text-lg font-mono tabular-nums flex-1 min-w-0"
                    data-testid="buyer-phone-input"
                    disabled={callBlocked}
                  />
                </div>
                <p className="text-xs text-slate-400 text-center">
                  Choisissez l’indicatif pays, puis saisissez votre numéro <strong className="font-medium text-slate-600">sans</strong> le préfixe international.
                </p>
                {isEmbedded && !callCertifiedAt ? (
                  <div
                    className="rounded-xl border border-slate-200 bg-slate-50/80 p-3"
                    data-testid="call-cert-inline"
                  >
                    <p className="mb-2 text-xs font-bold uppercase tracking-wide text-slate-600">
                      Engagement avant appel
                    </p>
                    {callCertFields}
                  </div>
                ) : null}
                <Button
                  type="button"
                  onClick={() => void handleLaunchCallClick()}
                  disabled={callBlocked}
                  className="btn-primary w-full h-12 rounded-xl text-base"
                  data-testid="trigger-mobile-call"
                >
                  <Phone className="w-4 h-4 mr-2" />Lancer l'appel
                </Button>
              </div>
            </>
          )}

          {step === "connecting" && (
            <div className="text-center py-6">
              <Loader2 className="w-12 h-12 mx-auto animate-spin text-[#2D7FF9] mb-6" />
              <h1 className="font-display font-black text-2xl mb-2">
                {callFlow === "prospect_first"
                  ? callAnswered && !clientAnswered
                    ? "Décrochez pour parler au client…"
                    : "Contact du prospect en cours…"
                  : callAnswered && !clientAnswered
                    ? "Mise en relation avec le prospect…"
                    : "Telnyx compose votre numéro…"}
              </h1>
              <p className="text-slate-500 text-sm">
                {callFlow === "prospect_first"
                  ? callAnswered && !clientAnswered
                    ? "Le client a répondu — décrochez tout de suite sur votre mobile pour être mis en relation."
                    : mobileMessage ||
                      "Si le client répond (pas un répondeur), votre mobile sonnera immédiatement."
                  : callAnswered && !clientAnswered
                    ? "Composition du prospect en cours — connexion dès qu’il décroche."
                    : "Décrochez sur votre téléphone. Le prospect est composé dès votre décroché."}
              </p>
              {queueRetryIn > 0 ? (
                <div className="mt-3 mx-auto max-w-sm rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-950">
                  Trop d&apos;appels en cours. Nouvel essai automatique dans <strong>{queueRetryIn}s</strong>.
                </div>
              ) : null}
              {clientAnswered ? (
                <div className="mt-3 mx-auto max-w-sm rounded-md border border-emerald-200 bg-emerald-50 px-3 py-2 text-xs text-emerald-900">
                  Client décroché.
                </div>
              ) : null}
              {mobileMessage ? (
                <div className="mt-4 mx-auto max-w-sm rounded-md border border-slate-200 bg-slate-50 px-3 py-2 text-xs text-slate-700">
                  {mobileMessage}
                </div>
              ) : null}
              <div className="mt-6 grid gap-2">
                <Button
                  className="btn-primary w-full h-12 rounded-xl text-base"
                  disabled
                  data-testid="i-answered"
                >
                  En attente du décroché…
                </Button>
                <Button
                  variant="outline"
                  className="w-full h-11 rounded-xl"
                  onClick={() => void abortFailedCall()}
                  data-testid="no-call-received"
                >
                  Annuler / revenir
                </Button>
              </div>
            </div>
          )}

          {step === "in_call" && (
            <div className="text-center py-4">
              {mobileMessage && (
                <div className="bg-emerald-50 border border-emerald-200 rounded-md p-3 mb-6 text-xs text-emerald-800">
                  📞 {mobileMessage}
                </div>
              )}
              {awaitingPhoneHangup ? (
                <div className="mb-6 mx-auto max-w-sm rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-950">
                  <strong>Raccrochez sur votre mobile.</strong> Dès que la ligne est coupée, vous pourrez indiquer si le RDV a été obtenu.
                </div>
              ) : null}
              <div className="relative w-32 h-32 mx-auto mb-6">
                <div className="absolute inset-0 rounded-full bg-emerald-500 pulse-ring flex items-center justify-center">
                  <Smartphone className="w-12 h-12 text-white" />
                </div>
              </div>
              <div className="text-xs tracking-[0.2em] uppercase font-bold text-emerald-600 mb-2 flex items-center justify-center gap-2">
                <Mic className="w-3 h-3" />Enregistrement chiffré · Rappel mobile
              </div>
              <h1 className="font-display font-black text-5xl tabular-nums tracking-tighter mb-2" data-testid="call-timer">{mm}:{ss}</h1>
              <p className="text-slate-500 text-sm mb-2">Appel en cours · durée informative uniquement</p>
              <Button
                variant="destructive"
                className="h-12 px-10 rounded-xl text-base mt-6 bg-red-600 hover:bg-red-700"
                onClick={requestPhoneHangup}
                disabled={awaitingPhoneHangup}
                data-testid="hangup-button"
              >
                {awaitingPhoneHangup ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    En attente du raccrochage…
                  </>
                ) : (
                  <>
                    <PhoneOff className="w-4 h-4 mr-2" />
                    J&apos;ai terminé l&apos;appel sur mon téléphone
                  </>
                )}
              </Button>
              <p className="text-[11px] text-slate-400 mt-3">
                Le choix « RDV obtenu / refus » s&apos;affiche uniquement après raccrochage réel sur votre ligne.
              </p>
            </div>
          )}

          {step === "ended" && !aiResult && (
            <div className="text-slate-600 text-center py-4">Appel terminé. Merci de répondre à la question…</div>
          )}

          {aiResult && !showPostRefusalVerdict && (
            <div className="mt-6 text-left bg-slate-50 border border-slate-200 rounded-md p-4" data-testid="ai-analysis-result">
              <div className="flex items-center gap-2 font-display font-bold mb-2">
                <ShieldCheck className="w-4 h-4 text-[#2D7FF9]" />Analyse IA
              </div>
              <div className="text-xs text-slate-600">
                {typeof aiResult.contact_harvest_violation === "boolean" ? (
                  <>
                    <strong>Anti-contournement (contact hors plateforme) :</strong>{" "}
                    {aiResult.contact_harvest_violation ? "Violation suspectée" : "Non détectée"}
                    <br />
                    <strong>Détail :</strong> {aiResult.reason || "—"}
                  </>
                ) : (
                  <>
                    <strong>Refus dossier légitime :</strong>{" "}
                    {aiResult.legitimate_refusal ? "Oui" : "Non"}
                    <br />
                    <strong>Détournement détecté :</strong> {aiResult.diversion_detected ? "Oui" : "Non"}
                    <br />
                    <strong>Raisonnement :</strong> {aiResult.reason || aiResult.reasoning || "—"}
                  </>
                )}
              </div>
            </div>
          )}
        </div>
      </div>

      <Dialog modal open={deferDialogOpen} onOpenChange={(v) => !deferSubmitting && setDeferDialogOpen(v)}>
        <DialogContent {...stackedFromShell}>
          <DialogHeader>
            <DialogTitle className="font-display text-lg sm:text-xl">Rappel client plus tard</DialogTitle>
            <DialogDescription>
              Vous confirmez que le client final a demandé à être rappelé plus tard. La réservation sera prolongée
              d’environ {deferInfo.extHours} h (jusqu’à {deferInfo.deferMax} fois pour cet achat). Le vendeur recevra un
              message sur le dossier et un e-mail si l’envoi est configuré (Resend).
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-2">
            <label className="text-xs font-medium text-slate-600" htmlFor="defer-note">
              Précision (optionnelle, max. 500 caractères)
            </label>
            <Textarea
              id="defer-note"
              value={deferNote}
              onChange={(e) => setDeferNote(e.target.value)}
              rows={3}
              maxLength={500}
              placeholder="Ex. rappel demain après 14h, messagerie…"
              className="resize-none"
            />
          </div>
          <DialogFooter className="gap-2 sm:gap-0">
            <Button type="button" variant="outline" onClick={() => setDeferDialogOpen(false)} disabled={deferSubmitting}>
              Annuler
            </Button>
            <Button
              type="button"
              className="btn-primary"
              disabled={deferSubmitting}
              onClick={() => void submitDeferClientCallback()}
            >
              {deferSubmitting ? <Loader2 className="w-4 h-4 animate-spin" /> : null}
              Confirmer la prolongation
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog
        modal
        open={showAiAnalysis}
        onOpenChange={(v) => {
          if (v) return;
          if (submitting) return;
          setShowAiAnalysis(false);
          setShowEnd(true);
        }}
      >
        <DialogContent data-testid="post-call-ai-analysis-dialog" {...stackedFromShell}>
          <DialogHeader>
            <DialogTitle className="font-display text-lg sm:text-xl flex items-center gap-2">
              <Sparkles className="w-5 h-5 text-[#2D7FF9]" aria-hidden />
              Analyse de l&apos;appel
            </DialogTitle>
            <DialogDescription>
              Votre refus est en cours de traitement : enregistrement puis analyse automatique pour trancher le remboursement.
            </DialogDescription>
          </DialogHeader>
          <div
            className="rounded-xl border-2 border-[#2D7FF9] bg-gradient-to-b from-[#2D7FF9]/10 to-white px-4 py-5 sm:px-5 sm:py-6 shadow-sm"
            role="status"
            aria-live="polite"
            data-testid="post-call-analysis-banner"
          >
            <div className="flex flex-col sm:flex-row sm:items-start gap-4">
              <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-full bg-[#2D7FF9]/15">
                {postCallSubmitPhase === "ai_analysis" ? (
                  <Sparkles className="h-6 w-6 text-[#2D7FF9] animate-pulse" aria-hidden />
                ) : (
                  <Loader2 className="h-6 w-6 text-[#2D7FF9] animate-spin" aria-hidden />
                )}
              </div>
              <div className="min-w-0 space-y-2 text-left">
                <p className="font-display font-bold text-base sm:text-lg text-slate-900 leading-snug">
                  {postCallSubmitPhase === "ai_analysis"
                    ? "Analyse par intelligence artificielle"
                    : "Préparation de l’enregistrement"}
                </p>
                <p className="text-sm text-slate-700 leading-relaxed">
                  {postCallSubmitPhase === "waiting_recording" ? (
                    <>
                      Récupération de l’<strong>enregistrement chiffré</strong> sur nos serveurs (Telnyx).{" "}
                      <strong className="text-slate-900">Ne fermez pas cette fenêtre.</strong>
                    </>
                  ) : (
                    <>
                      L’IA confronte l’enregistrement à votre explication (jusqu’à 1–2 minutes).{" "}
                      <strong className="text-slate-900">Ne fermez pas cette fenêtre</strong> avant le verdict.
                    </>
                  )}
                </p>
                <p className="text-xs text-slate-500 pt-1 flex items-center gap-2">
                  <Loader2 className="h-3.5 w-3.5 shrink-0 animate-spin text-[#2D7FF9]" aria-hidden />
                  Traitement en cours sur RdvMarket…
                </p>
              </div>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      <Dialog modal open={showEnd} onOpenChange={(v) => !v && !submitting && !showAiAnalysis && setShowEnd(v)}>
        <DialogContent data-testid="post-call-dialog" {...stackedFromShell}>
          <DialogHeader>
            <DialogTitle className="font-display text-lg sm:text-xl">Avez-vous obtenu le rendez-vous ?</DialogTitle>
            <DialogDescription>
              Cette réponse déclenche le déblocage des fonds ou le remboursement. Répondez après avoir raccroché sur votre
              téléphone mobile.
              {buyerRdvDeadlineAt ? (
                <>
                  {" "}
                  Sans décision avant le{" "}
                  <strong>
                    {new Date(buyerRdvDeadlineAt).toLocaleString("fr-FR", {
                      dateStyle: "short",
                      timeStyle: "short",
                    })}
                  </strong>{" "}
                  ({buyerRdvDecisionHours} h), tous les enregistrements seront analysés et le RDV pourra être réputé validé
                  (litige possible ensuite, sauf contournement constaté).
                </>
              ) : null}
            </DialogDescription>
          </DialogHeader>

          <div className="py-4 space-y-4">
            <div className="space-y-4">
            <div className="flex gap-3">
              <button
                type="button"
                onClick={() => setGotAppointment(true)}
                className={`flex-1 p-5 border-2 rounded-md transition-all ${gotAppointment === true ? "border-emerald-500 bg-emerald-50" : "border-[#E5E7EB] hover:border-emerald-300"}`}
                data-testid="outcome-yes"
              >
                <CheckCircle2 className="w-8 h-8 text-emerald-600 mx-auto mb-2" />
                <div className="font-display font-bold">Oui, RDV obtenu</div>
                <div className="text-xs text-slate-500 mt-1">Fonds libérés au vendeur</div>
              </button>
              <button
                type="button"
                onClick={() => setGotAppointment(false)}
                className={`flex-1 p-5 border-2 rounded-md transition-all ${gotAppointment === false ? "border-red-500 bg-red-50" : "border-[#E5E7EB] hover:border-red-300"}`}
                data-testid="outcome-no"
              >
                <XCircle className="w-8 h-8 text-red-600 mx-auto mb-2" />
                <div className="font-display font-bold">Non, refus</div>
                <div className="text-xs text-slate-500 mt-1">Remboursement ou non selon votre explication et l’appel</div>
              </button>
            </div>

            {gotAppointment === true && (
              <div className="rounded-lg border border-emerald-200 bg-emerald-50 px-4 py-3 text-xs text-emerald-900">
                Une fois confirmé, les informations du client (nom, téléphone, adresse…) sont disponibles dans{" "}
                <strong>Dashboard → Mes achats</strong>.
              </div>
            )}

            {gotAppointment === false && (
              <>
                <div className="rounded-lg border border-slate-200 bg-slate-50 px-3 py-2.5 text-xs text-slate-700 leading-snug">
                  Après confirmation, <strong className="font-semibold text-slate-900">l’enregistrement chiffré de l’appel</strong>{" "}
                  est analysé automatiquement par notre IA pour trancher le remboursement. Vous verrez ensuite le verdict et
                  pourrez, si besoin, <strong className="font-semibold text-slate-900">ouvrir un litige</strong> dans le délai
                  affiché sur votre tableau de bord.
                </div>
                <Select value={refusalReason} onValueChange={setRefusalReason}>
                  <SelectTrigger data-testid="refusal-reason-select"><SelectValue placeholder="Type de situation" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="pas_interesse">Client pas intéressé</SelectItem>
                    <SelectItem value="injoignable">Client injoignable</SelectItem>
                    <SelectItem value="faux_numero">Faux numéro / erreur</SelectItem>
                    <SelectItem value="deja_contacte">Déjà contacté par un autre</SelectItem>
                    <SelectItem value="hors_cible">Hors cible</SelectItem>
                    <SelectItem value="autre">Autre</SelectItem>
                  </SelectContent>
                </Select>
                <label htmlFor="refusal-explanation" className="sr-only">
                  Explication du refus de RDV
                </label>
                <div
                  id="refusal-explanation-hint"
                  className="rounded-lg border border-amber-200 bg-amber-50 px-3 py-2.5 text-xs text-amber-950 leading-snug"
                >
                  <strong className="font-semibold">Étape déterminante.</strong> Ce que vous écrivez ci-dessous sert à
                  décider si vous serez remboursé ou non : soyez <strong>précis</strong> et <strong>concis</strong> (faits
                  réels observés pendant l’appel).
                </div>
                <Textarea
                  id="refusal-explanation"
                  aria-describedby="refusal-explanation-hint"
                  placeholder="Pourquoi le RDV n’a pas été obtenu ? (obligatoire)"
                  rows={4}
                  data-testid="refusal-details"
                  value={refusalDetails}
                  onChange={(e) => setRefusalDetails(e.target.value)}
                />
              </>
            )}

            {deferInfo.canDefer ? (
              <div className="rounded-xl border border-slate-200 bg-slate-50/90 p-4 text-left space-y-2">
                <div className="flex items-start gap-2 text-sm text-slate-700">
                  <CalendarClock className="w-4 h-4 mt-0.5 shrink-0 text-[#2D7FF9]" />
                  <div>
                    <p className="font-medium">Le client demande un rappel plus tard ?</p>
                    <p className="text-xs text-slate-500 mt-1">
                      Prolongez la réservation d’environ <strong>{deferInfo.extHours} h</strong> (maximum {deferInfo.deferMax}{" "}
                      demandes · déjà {deferInfo.deferCount}). Le vendeur est informé sur le fil du dossier ; un e-mail part
                      aussi si l’envoi transactionnel est configuré (Resend).
                    </p>
                    {deferInfo.reservedUntil ? (
                      <p className="text-[11px] text-slate-400 mt-1">
                        Fin de réservation actuelle (heure locale) :{" "}
                        {new Date(deferInfo.reservedUntil).toLocaleString("fr-FR", {
                          dateStyle: "short",
                          timeStyle: "short",
                        })}
                      </p>
                    ) : null}
                  </div>
                </div>
                <Button
                  type="button"
                  variant="outline"
                  className="w-full h-11 rounded-xl border-[#2D7FF9]/40 text-[#2D7FF9] hover:bg-[#2D7FF9]/5"
                  disabled={submitting}
                  onClick={() => {
                    setDeferNote("");
                    setDeferDialogOpen(true);
                  }}
                  data-testid="post-call-defer-open"
                >
                  <CalendarClock className="w-4 h-4 mr-2" />
                  Rappel plus tard — prolonger la réservation
                </Button>
              </div>
            ) : null}
            </div>
          </div>

          <DialogFooter className="flex-col gap-2 sm:flex-col">
            <Button
              className="btn-primary w-full"
              onClick={submitOutcome}
              disabled={submitting || gotAppointment === null}
              data-testid="submit-call-outcome"
            >
              {submitting && gotAppointment === true ? (
                <span className="inline-flex items-center justify-center gap-2">
                  <Loader2 className="w-4 h-4 animate-spin shrink-0" aria-hidden />
                  Envoi…
                </span>
              ) : (
                "Confirmer"
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog
        modal
        open={showPostRefusalVerdict}
        onOpenChange={(open) => {
          if (open) return;
          setShowPostRefusalVerdict(false);
          setShowRating(true);
        }}
      >
        <DialogContent data-testid="post-refusal-verdict-dialog" {...stackedFromShell}>
          <DialogHeader>
            <DialogTitle className="font-display text-lg sm:text-xl flex items-start gap-2">
              <ShieldCheck className="w-6 h-6 shrink-0 text-[#2D7FF9] mt-0.5" aria-hidden />
              Verdict après analyse de l’appel
            </DialogTitle>
            <DialogDescription className="text-left text-sm text-slate-600">
              {isDisputeOpeningBlockedByPlatform(aiResult) ? (
                <>
                  Voici le résultat automatique basé sur l’enregistrement et votre compte rendu. La plateforme a constaté un
                  détournement ou une violation des règles de contact : <strong>l’ouverture d’un litige n’est pas possible</strong>.
                  Vous pouvez passer à la notation ci-dessous.
                </>
              ) : (
                <>
                  Voici le résultat automatique basé sur l’enregistrement et votre compte rendu. Vous pouvez accepter et passer à la
                  notation, ou ouvrir un litige si vous contestez cette décision (bouton balance sur « Mes achats », dans le délai
                  indiqué).
                </>
              )}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3 py-2">
            <p className="text-sm font-semibold text-slate-900">{refusalVerdictHeadline(lastOutcomeDecision)}</p>
            {aiResult ? (
              <div className="text-left bg-slate-50 border border-slate-200 rounded-md p-3 text-xs text-slate-600">
                {typeof aiResult.contact_harvest_violation === "boolean" ? (
                  <>
                    <strong>Anti-contournement :</strong>{" "}
                    {aiResult.contact_harvest_violation ? "Violation suspectée" : "Non détectée"}
                    <br />
                    <strong>Détail :</strong> {aiResult.reason || "—"}
                  </>
                ) : (
                  <>
                    <strong>Refus dossier légitime :</strong> {aiResult.legitimate_refusal ? "Oui" : "Non"}
                    <br />
                    <strong>Détournement détecté :</strong> {aiResult.diversion_detected ? "Oui" : "Non"}
                    <br />
                    <strong>Analyse :</strong> {aiResult.reason || aiResult.reasoning || "—"}
                  </>
                )}
              </div>
            ) : (
              <p className="text-xs text-slate-500">Aucun détail d’analyse supplémentaire.</p>
            )}
          </div>
          <DialogFooter className="gap-2 sm:flex-col sm:gap-2">
            <Button
              className="btn-primary w-full"
              onClick={() => {
                setShowPostRefusalVerdict(false);
                setShowRating(true);
              }}
              data-testid="post-refusal-verdict-ok"
            >
              OK — passer à la notation
            </Button>
            {!isDisputeOpeningBlockedByPlatform(aiResult) ? (
              <Button
                variant="outline"
                className="w-full border-amber-300 text-amber-950 hover:bg-amber-50"
                onClick={() => {
                  const typeLabel = REFUSAL_TYPE_LABELS[refusalReason] || refusalReason;
                  const expl = refusalDetails.trim();
                  const ai = aiResult;
                  let syn = "";
                  if (ai && typeof ai === "object") {
                    syn = String(ai.reason || ai.reasoning || "").trim();
                  }
                  const lines = [
                    "Contestation après refus de RDV — décision automatique contestée.",
                    refusalVerdictHeadline(lastOutcomeDecision),
                    syn ? `Synthèse IA : ${syn}` : null,
                    expl ? `Mon compte rendu (refus) — ${typeLabel} : ${expl}` : null,
                    "",
                    "Je demande l’examen manuel d’un administrateur (litige).",
                  ].filter(Boolean);
                  const defaultDescription = lines.join("\n\n").slice(0, 3500);
                  setShowPostRefusalVerdict(false);
                  if (typeof onRequestOpenDispute === "function") {
                    onRequestOpenDispute({ transactionId, defaultDescription });
                    return;
                  }
                  try {
                    sessionStorage.setItem(`rdm_dispute_prefill_${transactionId}`, defaultDescription);
                  } catch {
                    /* ignore */
                  }
                  navigate(`/dashboard?tab=buys&openDisputeTx=${encodeURIComponent(transactionId)}`);
                }}
                data-testid="post-refusal-open-dispute"
              >
                <span className="inline-flex items-center justify-center gap-2">
                  <Gavel className="w-4 h-4 shrink-0" aria-hidden />
                  Ouvrir un litige
                </span>
              </Button>
            ) : null}
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog modal open={showCallCert} onOpenChange={(v) => setShowCallCert(v)}>
        <DialogContent data-testid="call-cert-dialog" {...stackedFromShell}>
          <DialogHeader>
            <DialogTitle className="font-display text-lg sm:text-xl">Engagement avant appel</DialogTitle>
            <DialogDescription className="sr-only">
              Cochez chaque case pour confirmer votre engagement avant de lancer l’appel.
            </DialogDescription>
          </DialogHeader>

          <div className="py-4">{callCertFields}</div>

          <DialogFooter className="gap-2 sm:gap-2">
            <Button
              variant="outline"
              onClick={() => setShowCallCert(false)}
              data-testid="call-cert-cancel"
            >
              Annuler
            </Button>
            <Button
              className="btn-primary rounded-xl"
              disabled={!allCallChecked}
              onClick={async () => {
                setShowCallCert(false);
                const fn = pendingStart;
                setPendingStart(null);
                if (typeof fn === "function") await fn();
              }}
              data-testid="call-cert-confirm"
            >
              Je confirme et lance l'appel
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog
        modal
        open={platformRuleModalOpen}
        onOpenChange={(open) => {
          if (open) return;
          setPlatformRuleModalOpen(false);
          setShowRating(true);
        }}
      >
        <DialogContent
          data-testid="platform-rule-violation-dialog"
          {...stackedFromShell}
          className={cn(
            "max-w-lg min-w-0 border-amber-200 bg-amber-50/95 sm:max-w-lg",
            stackedFromShell.className,
          )}
          onPointerDownOutside={(e) => e.preventDefault()}
          onEscapeKeyDown={(e) => e.preventDefault()}
        >
          <DialogHeader className="min-w-0">
            <DialogTitle className="font-display text-lg sm:text-xl flex items-start gap-3 text-amber-950 min-w-0">
              <AlertTriangle className="w-7 h-7 shrink-0 text-amber-600 mt-0.5" aria-hidden />
              <span className="min-w-0 break-words">Décision plateforme</span>
            </DialogTitle>
            <DialogDescription className="text-left text-sm text-amber-950/90 leading-relaxed pt-2 break-words [overflow-wrap:anywhere]">
              {platformRuleModalText}
            </DialogDescription>
          </DialogHeader>
          <DialogFooter className="sm:justify-center min-w-0">
            <Button
              className="btn-primary w-full max-w-full sm:w-auto"
              onClick={() => {
                setPlatformRuleModalOpen(false);
                setShowRating(true);
              }}
              data-testid="platform-rule-acknowledge"
            >
              J’ai compris — passer à la notation
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog modal open={showRating} onOpenChange={(v) => !v && !submittingRating && skipRating()}>
        <DialogContent data-testid="post-call-rating-dialog" {...stackedFromShell}>
          <DialogHeader>
            <DialogTitle className="font-display text-lg sm:text-xl flex items-center gap-2">
              <Star className="w-6 h-6 text-[#FBBF24] fill-[#FBBF24]" />
              Notez le vendeur
            </DialogTitle>
            <DialogDescription>
              Votre note nourrit la réputation du vendeur (moyenne sur son profil). Pas de commentaire texte. Le vendeur pourra
              vous noter en retour.
            </DialogDescription>
          </DialogHeader>

          <div className="py-4 space-y-4">
            <div className="flex flex-col items-center gap-3 py-3">
              <div className="flex items-center gap-2" data-testid="rating-input-stars">
                <RatingStars value={ratingStars} onChange={setRatingStars} size="lg" />
              </div>
              <div className="text-sm text-slate-500">
                {ratingStars === 0 ? "Sélectionnez une note" :
                 ratingStars <= 2 ? "Expérience décevante" :
                 ratingStars === 3 ? "Correct" :
                 ratingStars === 4 ? "Bonne expérience" : "Excellent vendeur"}
              </div>
            </div>
          </div>

          <DialogFooter className="gap-2 sm:gap-2">
            <Button variant="outline" onClick={skipRating} disabled={submittingRating} data-testid="skip-rating">
              Plus tard
            </Button>
            <Button className="btn-primary" onClick={submitRating} disabled={submittingRating || ratingStars < 1} data-testid="submit-rating">
              {submittingRating ? <Loader2 className="w-4 h-4 animate-spin" /> : "Envoyer ma note"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
